import {
  ActionGroup,
  Button,
  Divider,
  Flex,
  FlexItem,
  PageSection,
  PageSectionVariants,
  Popover,
  Tab,
  Tabs,
  Title,
} from '@patternfly/react-core'
import { ExclamationTriangleIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { PatientCard, VisitForms, VisitSummaryCard } from '@app/components'
import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { EditFormModal } from '@app/components/form/edit-form'
import { DerivedValues } from '@app/components/visit/derived-values'
import { LaunchIntakeModal } from '@app/components/visit/list/launch_intake_modal'
import { ReportsList } from '@app/components/visit/reports'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import {
  useInstrument,
  useInstruments,
} from '@app/domain/instrument/instrument.hooks'
import type {
  IEntry,
  IInstrument,
} from '@app/domain/instrument/instrument.interface'
import { useUpdatePatient } from '@app/domain/patient/patient.hooks'
import { getVisitIntakeStartAction } from '@app/domain/visit/visit.helpers'
import {
  useEditEntryResponses,
  useStartInteraction,
  useGetVisitPdf,
} from '@app/domain/visit/visit.hooks'
import type {
  IVisit,
  IVisitDefinition,
} from '@app/domain/visit/visit.interface'
import { VisitStatusId } from '@app/domain/visit/visit.interface'
import type { RootState } from '@app/state'
import type { State as ConfigState } from '@app/state/config'
import * as Patients from '@app/state/patients'
import { actions as VisitListActions } from '@app/state/visit-list'
import * as Visits from '@app/state/visits'
import { ActionStatusId } from '@app/state/visits/state'
import { SmallButton } from '@app/utils/styles/small-button'

import { AddInstrumentModal } from './visit/add_instrument_modal'

interface RootParams {
  id: string
}

enum TabKeys {
  Instruments,
  DerivedValues,
  Reports,
}

export const VisitScreen = () => {
  const dispatch = useDispatch()
  const { updatePatient } = useUpdatePatient()

  const { editEntryResponses } = useEditEntryResponses()

  const { setInteraction } = useStartInteraction()

  const {
    getVisitPdf,
    visitPdfError,
    clearVisitPdfError,
    isVisitPdfLoading,
  } = useGetVisitPdf()

  const { id } = useParams<RootParams>()
  const [updatePatientStatus, setUpdatePatientStatus] = useState<
    Patients.PatientActionStatusId
  >()

  const config = useSelector<RootState, ConfigState>((state) => state.config)

  const entries = useSelector<RootState, Record<string, IEntry>>(
    (state) => state.visits.currentVisit.entries,
  )

  const isAddInstrumentModalOpen = useSelector<RootState, boolean>(
    (state) => state.visits.isInstrumentModalOpen,
  )

  const updateVisitStatus = useSelector<RootState, ActionStatusId>(
    (state) => state.visits.visitAction?.status,
  )
  const { visitData, loadVisitStatus } = useSelector<
    RootState,
    {
      visitData: {
        visit?: IVisit
        definition?: IVisitDefinition
        entries?: Record<string, IEntry>
      }
      loadVisitStatus: ActionStatusId
    }
  >((state) => ({
    visitData: state.visits.currentVisit,
    loadVisitStatus: state.visits.loadVisitStatus,
  }))

  const launchIntakeErrorMessage = useSelector<RootState, string>(
    (state) => state.visitList.launchIntakeErrorMessage,
  )
  const isLaunchIntakeModalOpen = useSelector<RootState, boolean>(
    (state) => state.visitList.isLaunchIntakeModalOpen,
  )

  const downloadReportErrorMessage = useSelector<RootState, string>(
    (state) => state.visits.downloadReportErrorMessage,
  )

  const isInstrumentPdfLoading = useSelector<RootState, boolean>(
    (state) => state.visits.isInstrumentPdfLoading,
  )

  const { instruments } = useInstruments()
  const [allInstruments, setAllInstruments] = useState<IInstrument[]>([])

  const [newInstrument, setNewInstrument] = useState<string>(undefined)

  const [selectedEntry, setSelectedEntry] = useState<IEntry>(undefined)
  const { instrument } = useInstrument(
    selectedEntry?.instrumentId,
    selectedEntry?.instrumentVersion,
  )

  const [isEditInstrumentModalOpen, setEditInstrumentModalOpen] = useState<
    boolean
  >(false)

  const [activeTabKey, setActiveTabKey] = useState<number>(TabKeys.Instruments)

  const [showCancelWarningContent, setShowCancelWarningContent] = useState(
    false,
  )

  const handleExitFromWarning = useCallback(() => {
    setShowCancelWarningContent(false)
  }, [])

  const handleGetVisitPdf = useCallback(
    (templateId: string) => {
      getVisitPdf(id, templateId)
    },
    [id, getVisitPdf],
  )

  const handleShowCancelWarning = useCallback(() => {
    setShowCancelWarningContent(true)
  }, [])

  const closeErrorAlert = () => {
    dispatch(Visits.actions.clearDownloadError())
  }

  const clearPatientActionStatus = useCallback(() => {
    setUpdatePatientStatus(null)
    dispatch(Patients.actions.clearActionStatus())
  }, [dispatch])

  const clearVisitActionStatus = useCallback(() => {
    dispatch(Visits.actions.clearActionStatus())
  }, [dispatch])

  const closeLaunchIntakeModal = useCallback(() => {
    dispatch(VisitListActions.closeLaunchIntakeModal())
  }, [dispatch])

  const downloadEntryPDF = useCallback(
    (entryId: string, templateId: string) => {
      dispatch(Visits.actions.downloadEntryPDF(entryId, templateId))
    },
    [dispatch],
  )

  const getDefinitions = useCallback(() => {
    dispatch(Visits.actions.getDefinitionList())
  }, [dispatch])

  const getVisitDefinition = useCallback(
    (visitId: string) => {
      dispatch(Visits.actions.getVisitDefinition(visitId))
    },
    [dispatch],
  )

  const launchIntake = useCallback(
    (visitId, selectedInteractions) => {
      dispatch(VisitListActions.launchIntake(visitId, selectedInteractions))
    },
    [dispatch],
  )

  const loadVisit = useCallback(
    (id: string) => {
      dispatch(Visits.actions.loadVisit(id))
    },
    [dispatch],
  )

  const openLaunchIntakeModal = useCallback(() => {
    dispatch(VisitListActions.openLaunchIntakeModal())
  }, [dispatch])

  const openAddInstrumentModal = useCallback(() => {
    dispatch(Visits.actions.addInstrumentModalOpen())
  }, [dispatch])

  const openEditInstrumentModal = useCallback((entry: IEntry) => {
    setSelectedEntry(entry)
    setEditInstrumentModalOpen(true)
  }, [])

  const onCloseEditInstrumentModal = useCallback(() => {
    setEditInstrumentModalOpen(false)
  }, [])

  const onCloseInstrumentModal = useCallback(() => {
    dispatch(Visits.actions.addInstrumentModalClosed())
  }, [dispatch])

  const updateVisit = useCallback(
    (id: string, visit: Visits.IApiUpdateVisit) => {
      dispatch(Visits.actions.updateVisit(id, visit))
    },
    [dispatch],
  )

  const reloadVisit = useCallback(() => {
    loadVisit(id)
    getVisitDefinition(id)
    getDefinitions()
  }, [getDefinitions, getVisitDefinition, id, loadVisit])

  const submitUpdatedVisitStatus = useCallback(
    (visitStatus: VisitStatusId) => {
      const { visit } = visitData
      dispatch(Visits.actions.updateVisitStatus(visit.id, visitStatus))
    },
    [dispatch, visitData],
  )

  const handleCancelVisit = useCallback(() => {
    submitUpdatedVisitStatus(VisitStatusId.Cancelled)
    setShowCancelWarningContent(false)
  }, [submitUpdatedVisitStatus])

  const handleCompleteVisit = useCallback(() => {
    submitUpdatedVisitStatus(VisitStatusId.Completed)
  }, [submitUpdatedVisitStatus])

  const handleRestoreCancelledVisit = useCallback(() => {
    submitUpdatedVisitStatus(VisitStatusId.NotStarted)
  }, [submitUpdatedVisitStatus])

  const handleSetInProgressVisit = useCallback(() => {
    submitUpdatedVisitStatus(VisitStatusId.InProgress)
  }, [submitUpdatedVisitStatus])

  useEffect(() => {
    reloadVisit()
  }, [getDefinitions, getVisitDefinition, id, loadVisit, reloadVisit])

  useEffect(() => {
    setAllInstruments(instruments)
  }, [instruments])

  const handleUpdatePatient = useCallback(
    (id: string, patient: Patients.IApiUpdatePatient) => {
      updatePatient(id, patient)
        .then(() => {
          reloadVisit()
          setUpdatePatientStatus(Patients.PatientActionStatusId.Succeeded)
        })
        .catch(() => {
          setUpdatePatientStatus(Patients.PatientActionStatusId.Failed)
        })
    },
    [reloadVisit, updatePatient],
  )

  const onSubmitEditInstrument = useCallback(
    (entry: IEntry, responses: Record<string, string>) => {
      editEntryResponses(entry.id, responses).then(() => {
        setEditInstrumentModalOpen(false)
        reloadVisit()
      })
      const { visit } = visitData
      if (
        visit.status === VisitStatusId.NotStarted ||
        visit.status === VisitStatusId.Ordered
      ) {
        handleSetInProgressVisit()
      }
    },
    [editEntryResponses, handleSetInProgressVisit, reloadVisit, visitData],
  )

  const [activeFormsTabKey, setActiveFormsTabKey] = useState<number>(0)

  const renderForms = useCallback(
    (
      visit: IVisit,
      definition: IVisitDefinition,
      formEntries: Array<IEntry>,
    ) => {
      if (definition && definition.allInstruments) {
        return (
          <VisitForms
            // Adding these more specific keys helps remount the component when the visit status
            // changes, otherwise, since the component does partial fetching of necessary data, it
            // remains stale.  A simpler implementation would lift all relevant state up and pass it
            // to components as needed.  The current implementation is a scattershot of
            // responsibilities that are unnecessarily complex so this is a stopgap to hold over
            // until a a robust implementation is made.
            // PC 2026-01-30
            key={`${visit?.id ?? 'NOVISIT'}-${visit?.status ?? 'NOSTATUS'}`}
            activeTabKey={activeFormsTabKey}
            onActiveTabKeyChange={setActiveFormsTabKey}
            definition={definition}
            entries={formEntries}
            newInstrumentAdded={newInstrument}
            openAddInstrumentModal={openAddInstrumentModal}
            openEditInstrumentModal={openEditInstrumentModal}
            visitId={visit?.id}
          />
        )
      } else {
        return <h1>This visit type has no instruments.</h1>
      }
    },
    [
      activeFormsTabKey,
      openAddInstrumentModal,
      openEditInstrumentModal,
      newInstrument,
      setActiveFormsTabKey,
    ],
  )

  const onStartIntake = useCallback(() => {
    openLaunchIntakeModal()
  }, [openLaunchIntakeModal])

  const onClose = useCallback(() => {
    closeLaunchIntakeModal()
  }, [closeLaunchIntakeModal])

  const startIntake = useCallback(
    (visit: IVisit, selectedInteractions: string[]) => {
      launchIntake(visit.id, selectedInteractions)
    },
    [launchIntake],
  )

  const onStartInteraction = useCallback(
    (visit: IVisit, selectedInteractions: string[]) => {
      setInteraction(visit.id, selectedInteractions).then(() => {
        handleSetInProgressVisit()
        closeLaunchIntakeModal()
      })
    },
    [setInteraction, handleSetInProgressVisit, closeLaunchIntakeModal],
  )

  const addInstrumentToVisit = useCallback(
    (instrumentId: string) => {
      dispatch(Visits.actions.addInstrumentToVisit(id, instrumentId))
      onCloseInstrumentModal()
      setNewInstrument(instrumentId)
      reloadVisit()
    },
    [dispatch, id, onCloseInstrumentModal, reloadVisit, setNewInstrument],
  )

  const setActiveTab = useCallback((_, selectedTab) => {
    setActiveTabKey(selectedTab)
  }, [])

  const warningPopOverContent = React.useMemo(() => {
    return (
      <>
        <Title headingLevel="h3" style={{ color: '#f0ab00' }}>
          <ExclamationTriangleIcon /> Warning
        </Title>
        <p>Once you cancel a visit, you will not be able to restart it.</p>

        <ActionGroup style={{ marginLeft: 'auto', marginTop: 10 }}>
          <Flex>
            <SmallButton
              style={{ marginRight: 5 }}
              key="cancel"
              variant="secondary"
              onClick={handleExitFromWarning}
            >
              Cancel
            </SmallButton>
            <SmallButton
              key="confirm"
              variant="primary"
              onClick={handleCancelVisit}
            >
              Continue
            </SmallButton>
          </Flex>
        </ActionGroup>
      </>
    )
  }, [handleCancelVisit, handleExitFromWarning])

  if (
    loadVisitStatus === ActionStatusId.Succeeded &&
    visitData?.visit?.definition &&
    config.clinic &&
    entries
  ) {
    const { visit } = visitData
    const startAction = getVisitIntakeStartAction(visit.status)
    let visitStatuses = []
    if (config?.clinic?.statuses) {
      visitStatuses = Object.values<string>(config.clinic.statuses)
    }

    return (
      <>
        <AddInstrumentModal
          addInstrumentToVisit={addInstrumentToVisit}
          definition={visitData.visit.definition}
          isModalOpen={isAddInstrumentModalOpen}
          instruments={allInstruments}
          onCancel={onCloseInstrumentModal}
        />

        {selectedEntry && instrument ? (
          // Again, we are forcing a remount of a component to ensure it fetches up-to-date state;
          // as in other places where this is done, it is a stopgap until a robust implementation is
          // made.
          // PC 2026-01-30
          <EditFormModal
            key={`${selectedEntry.id}-${selectedEntry.updatedAt.getTime()}`}
            entry={selectedEntry}
            isModalOpen={isEditInstrumentModalOpen}
            instrument={instrument}
            onCancel={onCloseEditInstrumentModal}
            onSubmit={onSubmitEditInstrument}
          />
        ) : null}
        <PageSection>
          <FlexItem>
            {startAction.show && (
              <Button style={{ marginRight: 5 }} onClick={onStartIntake}>
                {startAction.label}
              </Button>
            )}
            {visit.status === VisitStatusId.Cancelled && (
              <RequireFeatureFlag
                key="restore_cancelled_visit"
                feature={FeatureFlagId.RestoreCancelledVisit}
              >
                <Button
                  style={{ marginRight: 5 }}
                  onClick={handleRestoreCancelledVisit}
                >
                  Restore Cancelled Visit
                </Button>
              </RequireFeatureFlag>
            )}
            {visit.status === VisitStatusId.InProgress && (
              <Button onClick={handleCompleteVisit} style={{ marginRight: 5 }}>
                Complete
              </Button>
            )}

            <Popover
              aria-label="Cancel Visit Warning"
              showClose={false}
              hasAutoWidth
              isVisible={showCancelWarningContent}
              bodyContent={() => warningPopOverContent}
              withFocusTrap={false}
            >
              <Button
                onClick={handleShowCancelWarning}
                variant="secondary"
                isDisabled={
                  visit.status === VisitStatusId.Completed ||
                  visit.status === VisitStatusId.Cancelled
                }
              >
                Cancel Visit
              </Button>
            </Popover>
          </FlexItem>
          <Flex spaceItems={{ default: 'spaceItems2xl' }}>
            <FlexItem>
              <VisitSummaryCard
                visit={visit}
                visitStatuses={visitStatuses}
                updateVisit={updateVisit}
                updateStatus={updateVisitStatus}
                clearStatus={clearVisitActionStatus}
              />
            </FlexItem>
            <Divider isVertical={true} inset={{ default: 'insetMd' }} />
            <FlexItem>
              {visit.patient ? (
                <PatientCard
                  isSelectPatientAllowed={false}
                  onClearStatus={clearPatientActionStatus}
                  onUpdatePatient={handleUpdatePatient}
                  patient={visit.patient}
                  updateStatus={updatePatientStatus}
                />
              ) : null}
            </FlexItem>
          </Flex>
          <LaunchIntakeModal
            onClose={onClose}
            onStartIntake={startIntake}
            onStartInteraction={onStartInteraction}
            isOpen={isLaunchIntakeModalOpen}
            visit={visit}
            errorMessage={launchIntakeErrorMessage}
          />
        </PageSection>
        <PageSection variant={PageSectionVariants.light}>
          <Tabs
            activeKey={activeTabKey}
            onSelect={setActiveTab}
            isBox={true}
            role="region"
          >
            <Tab eventKey={TabKeys.Instruments} title="Instruments">
              {renderForms(
                visitData.visit,
                visitData.visit.definition,
                Object.values(entries),
              )}
            </Tab>
            <Tab eventKey={TabKeys.DerivedValues} title="Derived Values">
              <PageSection isFilled={true}>
                <DerivedValues visitId={visitData.visit.id} />
              </PageSection>
            </Tab>
            <Tab eventKey={TabKeys.Reports} title="Reports">
              <PageSection isFilled={true}>
                <ReportsList
                  definition={visitData.visit.definition}
                  entries={entries}
                  getEntryPdf={downloadEntryPDF}
                  getVisitPdf={handleGetVisitPdf}
                  isVisitPdfLoading={isVisitPdfLoading}
                  isInstrumentPdfLoading={isInstrumentPdfLoading}
                  downloadEntryPdfErrorMessage={downloadReportErrorMessage}
                  downloadVisitPdfErrorMessage={visitPdfError}
                  onCloseDownloadVisitPdfError={clearVisitPdfError}
                  onCloseDownloadEntryPdfError={closeErrorAlert}
                />
              </PageSection>
            </Tab>
          </Tabs>
        </PageSection>
      </>
    )
  } else if (loadVisitStatus === ActionStatusId.Pending) {
    return (
      <PageSection>
        <h1>Loading...</h1>
      </PageSection>
    )
  } else if (loadVisitStatus === ActionStatusId.Failed) {
    return (
      <PageSection>
        <h1>No Visit Found</h1>
      </PageSection>
    )
  } else {
    return null
  }
}
